/**
 * KB-018: Context budget utilization bar for KnowledgeAdmin.
 *
 * Shows: units used / max, breakdown (groups vs ungrouped),
 * color-coded progress bar: green (<60%), yellow (60-80%), red (>80%).
 *
 * KB-020: When isAdmin=true, renders an inline editor for the KB group limit.
 */
import { useState, useEffect } from "react";
import { getContextBudget, setKbLimit } from "../services/knowledge.js";

export function ContextBudgetBar({ personaId, refreshKey, pc = "#60a5fa", isAdmin = false, onLimitUpdated }) {
  const [budget, setBudget] = useState(null);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [limitSaving, setLimitSaving] = useState(false);
  const [limitError, setLimitError] = useState(null);

  useEffect(() => {
    if (!personaId) return;
    getContextBudget(personaId).then(setBudget).catch(() => setBudget(null));
  }, [personaId, refreshKey]);

  const handleSaveLimit = async () => {
    const val = limitInput.trim() === "" ? null : parseInt(limitInput, 10);
    if (val !== null && (isNaN(val) || val < 1 || val > 10_000)) {
      setLimitError("Enter a positive integer between 1 and 10 000, or leave blank to reset to default (100)");
      return;
    }
    setLimitSaving(true);
    setLimitError(null);
    try {
      await setKbLimit(personaId, val);
      setEditingLimit(false);
      // Refresh budget
      const fresh = await getContextBudget(personaId);
      setBudget(fresh);
      onLimitUpdated?.(val);
    } catch (err) {
      setLimitError(err?.message ?? "Failed to save");
    } finally {
      setLimitSaving(false);
    }
  };

  if (!budget) return null;

  const { groupCount, ungroupedCount, currentUnits, maxUnits, utilizationPercent, warning } = budget;

  // Color coding
  let barColor = "#22c55e"; // green
  if (utilizationPercent >= 80) barColor = "#ef4444"; // red
  else if (utilizationPercent >= 60) barColor = "#eab308"; // yellow

  const pct = Math.min(utilizationPercent, 100);

  return (
    <div style={{ background: "#0d1724", border: `1px solid ${warning ? barColor + "44" : "#1e2d45"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.05em" }}>
          Context Budget
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: barColor }}>
          {currentUnits} / {maxUnits} units ({utilizationPercent}%)
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 6, background: "#1e2d45", borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          background: barColor,
          borderRadius: 3,
          transition: "width 0.3s ease, background 0.3s ease",
        }} />
      </div>

      {/* Breakdown */}
      <div style={{ display: "flex", gap: 16, fontSize: 10, color: "#64748b", alignItems: "center" }}>
        <span>{groupCount} group{groupCount !== 1 ? "s" : ""}</span>
        <span>{ungroupedCount} ungrouped entr{ungroupedCount !== 1 ? "ies" : "y"}</span>
        {isAdmin && !editingLimit && (
          <button
            onClick={() => { setLimitInput(String(maxUnits)); setEditingLimit(true); setLimitError(null); }}
            style={{ marginLeft: "auto", fontSize: 10, background: "none", border: "none", color: "#3b82f6", cursor: "pointer", padding: "2px 4px", fontFamily: "inherit" }}
          >
            Edit limit
          </button>
        )}
      </div>

      {/* KB-020: Inline limit editor (admin only) */}
      {isAdmin && editingLimit && (
        <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="number"
            min="1"
            max="10000"
            value={limitInput}
            onChange={e => setLimitInput(e.target.value)}
            placeholder="e.g. 100"
            style={{ width: 80, padding: "4px 8px", background: "#090e1a", border: "1px solid #3b82f6", borderRadius: 6, color: "#e2e8f0", fontSize: 12, fontFamily: "inherit" }}
          />
          <button
            onClick={handleSaveLimit}
            disabled={limitSaving}
            style={{ padding: "4px 10px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, cursor: limitSaving ? "not-allowed" : "pointer", fontFamily: "inherit" }}
          >
            {limitSaving ? "…" : "Save"}
          </button>
          <button
            onClick={() => { setEditingLimit(false); setLimitError(null); }}
            style={{ padding: "4px 8px", background: "none", border: "1px solid #1e2d45", borderRadius: 6, color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
          >
            Cancel
          </button>
        </div>
      )}
      {isAdmin && limitError && (
        <div style={{ marginTop: 4, fontSize: 10, color: "#ef4444" }}>{limitError}</div>
      )}

      {/* Warning message */}
      {budget.warningMessage && (
        <div style={{
          marginTop: 8,
          fontSize: 11,
          color: warning === "exceeded" ? "#ef4444" : "#eab308",
          background: warning === "exceeded" ? "#ef444411" : "#eab30811",
          padding: "6px 10px",
          borderRadius: 6,
          lineHeight: 1.4,
        }}>
          {budget.warningMessage}
        </div>
      )}
    </div>
  );
}

export default ContextBudgetBar;
